import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../lib/api';
import { 
  FileText, 
  Plus,
  Printer,
  ArrowClockwise,
  User,
  MagnifyingGlass,
  Calendar,
  Receipt,
  X,
  Eye,
  Download,
  Stethoscope,
  FirstAidKit
} from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function SuperbillPage() {
  const { user } = useAuth();
  const [superbills, setSuperbills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedSuperbill, setSelectedSuperbill] = useState(null);
  
  // Create form
  const [patients, setPatients] = useState([]);
  const [patientInvoices, setPatientInvoices] = useState([]);
  const [icd10Codes, setIcd10Codes] = useState([]);
  const [cptCodes, setCptCodes] = useState([]);
  const [formData, setFormData] = useState({
    patient_id: '',
    invoice_id: '',
    service_date: new Date().toISOString().split('T')[0],
    diagnosis_codes: [],
    procedure_codes: [],
    notes: ''
  });
  const [creating, setCreating] = useState(false);
  const [dxSearch, setDxSearch] = useState('');
  const [cptSearch, setCptSearch] = useState('');

  useEffect(() => {
    fetchSuperbills();
    if (user?.role !== 'patient') {
      fetchPatients();
    }
  }, [user]);

  const fetchSuperbills = async () => {
    try {
      const response = await api.get('/billing/superbills');
      setSuperbills(response.data.superbills || []);
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error fetching superbills:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPatients = async () => {
    try {
      const response = await api.get('/admin/users?role=patient');
      const patientList = Array.isArray(response.data) ? response.data : (response.data.users || []);
      setPatients(patientList);
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error fetching patients:', error);
    }
  };

  const fetchPatientInvoices = async (patientId) => {
    try {
      const response = await api.get(`/billing/invoices?patient_id=${patientId}`);
      setPatientInvoices(response.data || []);
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error fetching invoices:', error);
    }
  };

  const searchICD10 = async (query) => {
    setDxSearch(query);
    if (query.length < 2) {
      setIcd10Codes([]);
      return;
    }
    try {
      const response = await api.get(`/billing/codes/icd10?search=${query}`);
      setIcd10Codes(response.data.codes || []);
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error searching ICD-10:', error);
    }
  };

  const searchCPT = async (query) => {
    setCptSearch(query);
    if (query.length < 2) {
      setCptCodes([]);
      return;
    }
    try {
      const response = await api.get(`/billing/codes/cpt?search=${query}`);
      setCptCodes(response.data.codes || []);
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error searching CPT:', error);
    }
  };

  const handlePatientChange = (patientId) => {
    setFormData(prev => ({ ...prev, patient_id: patientId, invoice_id: '' }));
    if (patientId) {
      fetchPatientInvoices(patientId);
    } else {
      setPatientInvoices([]);
    }
  };

  const handleInvoiceChange = async (invoiceId) => {
    setFormData(prev => ({ ...prev, invoice_id: invoiceId }));
    
    if (invoiceId) {
      const invoice = patientInvoices.find(inv => inv.id === invoiceId);
      if (invoice) {
        setFormData(prev => ({
          ...prev,
          diagnosis_codes: invoice.diagnosis_codes || [],
          procedure_codes: (invoice.items || []).map(item => ({
            code: item.code,
            description: item.description,
            modifiers: item.modifiers || [],
            quantity: item.quantity || 1,
            fee: item.unit_price || item.total || 0
          })),
          service_date: invoice.created_at?.split('T')[0] || prev.service_date
        }));
      }
    }
  };

  const addDiagnosisCode = (code) => {
    if (!formData.diagnosis_codes.find(d => d.code === code.code)) {
      setFormData(prev => ({
        ...prev,
        diagnosis_codes: [...prev.diagnosis_codes, { code: code.code, description: code.description }]
      }));
    }
    setDxSearch('');
    setIcd10Codes([]);
  };

  const removeDiagnosisCode = (code) => {
    setFormData(prev => ({
      ...prev,
      diagnosis_codes: prev.diagnosis_codes.filter(d => d.code !== code)
    }));
  };

  const addProcedureCode = (code) => {
    if (!formData.procedure_codes.find(p => p.code === code.code)) {
      setFormData(prev => ({
        ...prev,
        procedure_codes: [...prev.procedure_codes, {
          code: code.code,
          description: code.description,
          modifiers: [],
          quantity: 1,
          fee: code.fee || 0
        }]
      }));
    }
    setCptSearch('');
    setCptCodes([]);
  };

  const removeProcedureCode = (code) => {
    setFormData(prev => ({
      ...prev,
      procedure_codes: prev.procedure_codes.filter(p => p.code !== code)
    }));
  };

  const updateProcedureCode = (code, field, value) => {
    setFormData(prev => ({
      ...prev,
      procedure_codes: prev.procedure_codes.map(p => 
        p.code === code ? { ...p, [field]: value } : p
      )
    }));
  };

  const createSuperbill = async () => {
    if (!formData.patient_id) {
      toast.error('Please select a patient');
      return;
    }
    
    if (formData.diagnosis_codes.length === 0 || formData.procedure_codes.length === 0) {
      toast.error('Please add at least one diagnosis and one procedure code');
      return;
    }
    
    setCreating(true);
    try {
      const response = await api.post('/billing/superbill/generate', formData);
      toast.success('Superbill generated successfully');
      setShowCreateModal(false);
      resetForm();
      fetchSuperbills();
      
      // Open the superbill for printing
      printSuperbill(response.data.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate superbill');
    } finally {
      setCreating(false);
    }
  };

  const resetForm = () => {
    setFormData({
      patient_id: '',
      invoice_id: '',
      service_date: new Date().toISOString().split('T')[0],
      diagnosis_codes: [],
      procedure_codes: [],
      notes: ''
    });
    setPatientInvoices([]);
    setDxSearch('');
    setCptSearch('');
    setIcd10Codes([]);
    setCptCodes([]);
  };

  const printSuperbill = (superbillId) => {
    window.open(
      `${process.env.REACT_APP_BACKEND_URL}/api/billing/superbills/${superbillId}/html`,
      '_blank'
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  // Calculate total
  const calculateTotal = () => {
    return formData.procedure_codes.reduce((sum, p) => sum + (p.fee * p.quantity), 0);
  };

  return (
    <div className="p-8" data-testid="superbill-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>
            Superbills
          </h1>
          <p className="text-slate-500">Generate superbills for patient self-submission to insurance</p>
        </div>
        
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-teal flex items-center gap-2"
        >
          <Plus size={18} />
          Create Superbill
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="card-clinical p-4">
          <p className="text-sm text-slate-500">Total Superbills</p>
          <p className="text-2xl font-bold text-slate-900">{superbills.length}</p>
        </div>
        <div className="card-clinical p-4">
          <p className="text-sm text-slate-500">This Month</p>
          <p className="text-2xl font-bold text-teal-600">
            {superbills.filter(s => {
              const created = new Date(s.created_at);
              const now = new Date();
              return created.getMonth() === now.getMonth() && created.getFullYear() === now.getFullYear();
            }).length}
          </p>
        </div>
        <div className="card-clinical p-4">
          <p className="text-sm text-slate-500">Total Charges</p>
          <p className="text-2xl font-bold text-slate-900">
            {formatCurrency(superbills.reduce((sum, s) => sum + (s.totals?.total || 0), 0))}
          </p>
        </div>
        <div className="card-clinical p-4">
          <p className="text-sm text-slate-500">Avg per Superbill</p>
          <p className="text-2xl font-bold text-slate-900">
            {formatCurrency(superbills.length > 0 
              ? superbills.reduce((sum, s) => sum + (s.totals?.total || 0), 0) / superbills.length 
              : 0
            )}
          </p>
        </div>
      </div>

      {/* Superbills List */}
      <div className="card-clinical overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Superbill #</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Patient</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Service Date</th>
              <th className="text-center py-3 px-4 text-sm font-medium text-slate-600">Dx Codes</th>
              <th className="text-center py-3 px-4 text-sm font-medium text-slate-600">Proc Codes</th>
              <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">Total</th>
              <th className="text-center py-3 px-4 text-sm font-medium text-slate-600">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="7" className="py-8 text-center text-slate-500">
                  <ArrowClockwise size={24} className="animate-spin mx-auto mb-2" />
                  Loading superbills...
                </td>
              </tr>
            ) : superbills.length === 0 ? (
              <tr>
                <td colSpan="7" className="py-8 text-center text-slate-500">
                  <FileText size={48} className="mx-auto mb-2 text-slate-300" />
                  No superbills generated yet
                </td>
              </tr>
            ) : (
              superbills.map((sb) => (
                <tr key={sb.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="py-3 px-4">
                    <span className="font-mono text-sm text-teal-600">{sb.superbill_number}</span>
                  </td>
                  <td className="py-3 px-4">
                    <p className="font-medium text-slate-900">{sb.patient?.name}</p>
                  </td>
                  <td className="py-3 px-4 text-sm text-slate-600">
                    {formatDate(sb.service_date)}
                  </td>
                  <td className="py-3 px-4 text-center">
                    <span className="px-2 py-1 bg-red-50 text-red-700 rounded-full text-xs font-medium">
                      {sb.diagnosis_codes?.length || 0}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center">
                    <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">
                      {sb.procedure_codes?.length || 0}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-right font-medium">
                    {formatCurrency(sb.totals?.total)}
                  </td>
                  <td className="py-3 px-4 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        onClick={() => setSelectedSuperbill(sb)}
                        className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                        title="View Details"
                      >
                        <Eye size={18} className="text-slate-500" />
                      </button>
                      <button
                        onClick={() => printSuperbill(sb.id)}
                        className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                        title="Print Superbill"
                      >
                        <Printer size={18} className="text-teal-600" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create Superbill Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-teal-100 rounded-lg">
                  <Receipt size={24} className="text-teal-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-900">Create Superbill</h2>
                  <p className="text-sm text-slate-500">Generate a superbill for insurance submission</p>
                </div>
              </div>
              <button onClick={() => { setShowCreateModal(false); resetForm(); }} className="p-2 hover:bg-slate-100 rounded-lg">
                <X size={20} />
              </button>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
              {/* Left Column - Patient & Invoice */}
              <div className="space-y-4">
                {/* Patient Selection */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Select Patient *</label>
                  <select
                    value={formData.patient_id}
                    onChange={(e) => handlePatientChange(e.target.value)}
                    className="input-clinical"
                  >
                    <option value="">Choose a patient...</option>
                    {patients.map((p) => (
                      <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>
                    ))}
                  </select>
                </div>

                {/* Invoice Selection (Optional) */}
                {formData.patient_id && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Import from Invoice (optional)
                    </label>
                    <select
                      value={formData.invoice_id}
                      onChange={(e) => handleInvoiceChange(e.target.value)}
                      className="input-clinical"
                    >
                      <option value="">Start fresh or select an invoice...</option>
                      {patientInvoices.map((inv) => (
                        <option key={inv.id} value={inv.id}>
                          {formatDate(inv.created_at)} - {formatCurrency(inv.subtotal)}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Service Date */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Date of Service *</label>
                  <input
                    type="date"
                    value={formData.service_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, service_date: e.target.value }))}
                    className="input-clinical"
                  />
                </div>

                {/* Diagnosis Codes */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    <Stethoscope size={16} className="inline mr-1" />
                    Diagnosis Codes (ICD-10) *
                  </label>
                  <div className="relative">
                    <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type="text"
                      value={dxSearch}
                      onChange={(e) => searchICD10(e.target.value)}
                      placeholder="Search ICD-10 codes..."
                      className="input-clinical pl-10"
                    />
                    {icd10Codes.length > 0 && (
                      <div className="absolute top-full left-0 right-0 bg-white border rounded-lg shadow-lg z-10 max-h-40 overflow-y-auto">
                        {icd10Codes.slice(0, 10).map((code) => (
                          <button
                            key={code.code}
                            onClick={() => addDiagnosisCode(code)}
                            className="w-full p-2 text-left hover:bg-slate-50 text-sm border-b last:border-0"
                          >
                            <span className="font-mono text-red-600">{code.code}</span>
                            <span className="text-slate-600 ml-2">{code.description?.substring(0, 40)}...</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {/* Selected Diagnosis Codes */}
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.diagnosis_codes.map((dx) => (
                      <span key={dx.code} className="inline-flex items-center gap-1 px-2 py-1 bg-red-50 text-red-700 rounded text-sm">
                        <span className="font-mono font-bold">{dx.code}</span>
                        <button onClick={() => removeDiagnosisCode(dx.code)} className="hover:text-red-900">
                          <X size={14} />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Right Column - Procedure Codes */}
              <div className="space-y-4">
                {/* Procedure Codes */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    <FirstAidKit size={16} className="inline mr-1" />
                    Procedure Codes (CPT) *
                  </label>
                  <div className="relative">
                    <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type="text"
                      value={cptSearch}
                      onChange={(e) => searchCPT(e.target.value)}
                      placeholder="Search CPT codes..."
                      className="input-clinical pl-10"
                    />
                    {cptCodes.length > 0 && (
                      <div className="absolute top-full left-0 right-0 bg-white border rounded-lg shadow-lg z-10 max-h-40 overflow-y-auto">
                        {cptCodes.slice(0, 10).map((code) => (
                          <button
                            key={code.code}
                            onClick={() => addProcedureCode(code)}
                            className="w-full p-2 text-left hover:bg-slate-50 text-sm border-b last:border-0"
                          >
                            <span className="font-mono text-blue-600">{code.code}</span>
                            <span className="text-slate-600 ml-2">{code.description?.substring(0, 30)}...</span>
                            <span className="text-teal-600 ml-2">{formatCurrency(code.fee)}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Selected Procedure Codes */}
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {formData.procedure_codes.map((proc) => (
                    <div key={proc.code} className="p-3 bg-blue-50 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-mono font-bold text-blue-700">{proc.code}</span>
                        <button onClick={() => removeProcedureCode(proc.code)} className="text-slate-400 hover:text-red-500">
                          <X size={16} />
                        </button>
                      </div>
                      <p className="text-xs text-slate-600 mb-2">{proc.description?.substring(0, 50)}...</p>
                      <div className="flex items-center gap-3">
                        <div className="flex-1">
                          <label className="text-xs text-slate-500">Qty</label>
                          <input
                            type="number"
                            min="1"
                            value={proc.quantity}
                            onChange={(e) => updateProcedureCode(proc.code, 'quantity', parseInt(e.target.value) || 1)}
                            className="w-full mt-1 px-2 py-1 border rounded text-sm"
                          />
                        </div>
                        <div className="flex-1">
                          <label className="text-xs text-slate-500">Fee</label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={proc.fee}
                            onChange={(e) => updateProcedureCode(proc.code, 'fee', parseFloat(e.target.value) || 0)}
                            className="w-full mt-1 px-2 py-1 border rounded text-sm"
                          />
                        </div>
                        <div className="flex-1">
                          <label className="text-xs text-slate-500">Total</label>
                          <p className="mt-1 font-bold text-teal-600">{formatCurrency(proc.quantity * proc.fee)}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Total */}
                {formData.procedure_codes.length > 0 && (
                  <div className="p-4 bg-teal-50 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-teal-800">Total Charges</span>
                      <span className="text-2xl font-bold text-teal-700">{formatCurrency(calculateTotal())}</span>
                    </div>
                  </div>
                )}

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Notes (optional)</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    className="input-clinical"
                    rows={2}
                    placeholder="Any additional notes..."
                  />
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 mt-6">
              <button onClick={() => { setShowCreateModal(false); resetForm(); }} className="flex-1 btn-outline">
                Cancel
              </button>
              <button
                onClick={createSuperbill}
                disabled={!formData.patient_id || formData.diagnosis_codes.length === 0 || formData.procedure_codes.length === 0 || creating}
                className="flex-1 btn-teal flex items-center justify-center gap-2"
              >
                {creating ? <ArrowClockwise size={18} className="animate-spin" /> : <Receipt size={18} />}
                Generate Superbill
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Superbill Detail Modal */}
      {selectedSuperbill && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-slate-900">Superbill Details</h2>
                <p className="text-teal-600 font-mono">{selectedSuperbill.superbill_number}</p>
              </div>
              <button onClick={() => setSelectedSuperbill(null)} className="p-2 hover:bg-slate-100 rounded-lg">
                <X size={20} />
              </button>
            </div>

            {/* Patient Info */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-xs text-slate-500 mb-1">Patient</p>
                <p className="font-medium">{selectedSuperbill.patient?.name}</p>
                <p className="text-sm text-slate-600">{selectedSuperbill.patient?.email}</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-xs text-slate-500 mb-1">Date of Service</p>
                <p className="font-medium">{formatDate(selectedSuperbill.service_date)}</p>
              </div>
            </div>

            {/* Diagnosis Codes */}
            <div className="mb-6">
              <h3 className="font-medium text-slate-900 mb-2">Diagnosis Codes</h3>
              <div className="flex flex-wrap gap-2">
                {selectedSuperbill.diagnosis_codes?.map((dx, idx) => (
                  <span key={idx} className="px-3 py-1 bg-red-50 text-red-700 rounded-full text-sm">
                    <span className="font-mono font-bold">{dx.code}</span>
                    <span className="ml-1 text-red-600">- {dx.description?.substring(0, 30)}...</span>
                  </span>
                ))}
              </div>
            </div>

            {/* Procedure Codes */}
            <div className="mb-6">
              <h3 className="font-medium text-slate-900 mb-2">Procedure Codes</h3>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2">Code</th>
                    <th className="text-left py-2">Description</th>
                    <th className="text-center py-2">Qty</th>
                    <th className="text-right py-2">Fee</th>
                    <th className="text-right py-2">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedSuperbill.procedure_codes?.map((proc, idx) => (
                    <tr key={idx} className="border-b border-slate-100">
                      <td className="py-2 font-mono text-blue-600">{proc.code}</td>
                      <td className="py-2 text-slate-600">{proc.description?.substring(0, 30)}...</td>
                      <td className="py-2 text-center">{proc.quantity}</td>
                      <td className="py-2 text-right">{formatCurrency(proc.fee)}</td>
                      <td className="py-2 text-right font-medium">{formatCurrency(proc.quantity * proc.fee)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-teal-50">
                    <td colSpan="4" className="py-3 text-right font-medium">Total:</td>
                    <td className="py-3 text-right font-bold text-teal-700 text-lg">
                      {formatCurrency(selectedSuperbill.totals?.total)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button onClick={() => setSelectedSuperbill(null)} className="flex-1 btn-outline">
                Close
              </button>
              <button
                onClick={() => printSuperbill(selectedSuperbill.id)}
                className="flex-1 btn-teal flex items-center justify-center gap-2"
              >
                <Printer size={18} />
                Print Superbill
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
